import { Camera, Download, FileText, FileWarning, ImagePlus, Loader2, Trash2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/ui/dialog"
import { formatDuration, formatSize, type MediaItem } from "../model/media-items"
import { captureFrame } from "../model/staged-media"

const KIND_LABEL: Record<MediaItem["kind"], string> = {
	photo: "Фото",
	video: "Видео",
	file: "Файл",
}

/**
 * Просмотр файла поста. У видео здесь же меняется обложка: картинкой с диска или
 * кадром, на котором остановлено видео.
 */
export function MediaViewer({
	item,
	onClose,
	onPoster,
	onRemove,
	busy,
}: {
	item: MediaItem | null
	onClose: () => void
	onPoster: (item: MediaItem, file: File) => Promise<void> | void
	onRemove: (item: MediaItem) => void
	busy: boolean
}) {
	const video = useRef<HTMLVideoElement>(null)
	const posterInput = useRef<HTMLInputElement>(null)
	const [capturing, setCapturing] = useState(false)
	const [broken, setBroken] = useState(false)
	const [posterBroken, setPosterBroken] = useState(false)

	// Новый файл открывается с чистого листа: прошлая ошибка к нему не относится.
	useEffect(() => {
		setBroken(false)
		setPosterBroken(false)
	}, [item?.key, item?.posterUrl])

	const takeFrame = async () => {
		if (!item || !video.current) return
		setCapturing(true)
		try {
			video.current.pause()
			const file = await captureFrame(video.current, `${item.name.replace(/\.[^.]+$/, "")}-poster.jpg`)
			await onPoster(item, file)
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Не удалось взять кадр")
		} finally {
			setCapturing(false)
		}
	}

	const facts = item
		? [
				KIND_LABEL[item.kind],
				item.width && item.height ? `${item.width}×${item.height}` : null,
				formatDuration(item.duration),
				formatSize(item.size),
				item.source === "staged" ? "ещё не загружен" : null,
			].filter(Boolean)
		: []

	return (
		<Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="grid-cols-1 max-w-5xl gap-0 overflow-hidden p-0 sm:rounded-2xl">
				{item ? (
					<div className="grid lg:grid-cols-[minmax(0,1fr)_300px]">
						<div className="grid min-h-64 min-w-0 place-items-center bg-black">
							{broken ? (
								<div className="flex max-w-sm flex-col items-center gap-3 p-10 text-center text-white/70">
									<FileWarning className="size-10" />
									<p className="text-sm font-medium text-white">Файл не открывается</p>
									<p className="text-xs leading-snug">
										Хранилище не отдало его: файл удалён или ещё не докачался. Загрузите его
										заново, если он нужен в посте.
									</p>
								</div>
							) : item.kind === "video" ? (
								<video
									ref={video}
									key={item.url}
									src={item.url}
									poster={posterBroken ? undefined : (item.posterUrl ?? undefined)}
									onError={() => setBroken(true)}
									controls
									playsInline
									preload="metadata"
									className="max-h-[70vh] w-full"
								/>
							) : item.kind === "photo" ? (
								<img
									src={item.url}
									alt={item.name}
									onError={() => setBroken(true)}
									className="max-h-[70vh] w-full object-contain"
								/>
							) : (
								<div className="flex flex-col items-center gap-3 p-10 text-white/70">
									<FileText className="size-10" />
									<span className="text-sm">Предпросмотра для этого файла нет</span>
								</div>
							)}
						</div>
						<aside className="flex flex-col gap-5 p-5">
							<div>
								<DialogTitle className="text-base leading-snug break-words">{item.name}</DialogTitle>
								<DialogDescription className="mt-1">{facts.join(" · ")}</DialogDescription>
							</div>

							{item.kind === "video" ? (
								<section className="space-y-3">
									<h3 className="text-sm font-medium">Обложка видео</h3>
									<div className="aspect-video overflow-hidden rounded-lg bg-muted">
										{item.posterUrl && !posterBroken ? (
											<img
												src={item.posterUrl}
												alt=""
												onError={() => setPosterBroken(true)}
												className="size-full object-cover"
											/>
										) : (
											<div className="grid size-full place-items-center text-xs text-muted-foreground">
												Обложки нет, Telegram покажет первый кадр
											</div>
										)}
									</div>
									<input
										ref={posterInput}
										type="file"
										accept="image/jpeg,image/png,image/webp"
										className="sr-only"
										onChange={(event) => {
											const file = event.target.files?.[0]
											event.target.value = ""
											if (file) void onPoster(item, file)
										}}
									/>
									<div className="grid gap-2">
										<Button
											type="button"
											variant="secondary"
											size="sm"
											disabled={busy || capturing || broken}
											onClick={takeFrame}
										>
											{capturing ? <Loader2 className="animate-spin" /> : <Camera />}
											Сделать обложкой текущий кадр
										</Button>
										<Button
											type="button"
											variant="secondary"
											size="sm"
											disabled={busy || capturing}
											onClick={() => posterInput.current?.click()}
										>
											<ImagePlus />
											Загрузить картинку
										</Button>
									</div>
									<p className="text-xs leading-snug text-muted-foreground">
										Остановите видео на нужном месте и нажмите «Сделать обложкой текущий кадр».
									</p>
								</section>
							) : null}

							<div className={cn("mt-auto grid gap-2", item.kind !== "video" && "lg:mt-auto")}>
								{item.source === "existing" ? (
									<Button asChild variant="ghost" size="sm" className="justify-start">
										<a href={item.url} target="_blank" rel="noreferrer" download>
											<Download />
											Скачать оригинал
										</a>
									</Button>
								) : null}
								<Button
									type="button"
									variant="ghost"
									size="sm"
									disabled={busy}
									onClick={() => onRemove(item)}
									className="justify-start text-red-400 hover:text-red-300"
								>
									<Trash2 />
									Убрать из поста
								</Button>
							</div>
						</aside>
					</div>
				) : null}
			</DialogContent>
		</Dialog>
	)
}
